import { Avatar, Button, ButtonBase, Stack, Typography } from '@mui/material';
import { ControlledAutocomplete } from 'Components/ControlledAutoComplete';
import { AdvancedSearchContext } from 'Context/MedicalReferralsContext/AdvancedSearchProvider';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PurpleSearchIcon, PurpleX } from 'Images';
import { useContext, useState } from 'react';
import { IReferralsState } from 'reducer/MedicalReferrals/ReferralReducer';

interface ISearchBySubject {
  dispatchReferrals: Function;
  setTextSearchValue: (value: string) => void;
  referrals: IReferralsState;
}

export const SearchBySubject = ({
  dispatchReferrals,
  setTextSearchValue,
  referrals,
}: ISearchBySubject) => {
  const isDesktop = useIsDesktop();
  const [inputValue, setInputValue] = useState('');
  const { dispatchAdvancedSearch } = useContext(AdvancedSearchContext);

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicalReferrals
  ).search;

  const submitSearch = (query: string | null) => {
    if (query === null) query = '';

    setTextSearchValue(query);
    if (query.length < 1) return;
    dispatchAdvancedSearch({ type: 'RESET' });
    dispatchReferrals({
      type: 'SEARCH_REFERRAL',
      payload: { value: query },
    });
    dispatchAdvancedSearch({
      type: 'ACTIVE_SEARCH_TEXT',
      payload: { value: true },
    });
  };

  const options = [
    ...new Set(
      [...referrals.validReferrals, ...referrals.otherReferrals]
        .map((referral) => referral.part)
        .map((title) => title.trim())
    ),
  ].filter((title) => title.includes(inputValue ?? ''));

  return (
    <ControlledAutocomplete
      freeSolo
      label={dictionary.searchFilter}
      options={options}
      setValue={(value: string | null) => {
        submitSearch(value ?? '');
      }}
      inputValue={inputValue}
      inputLabelProps={{
        shrink: false,
        sx: { display: inputValue?.length > 0 ? 'none' : 'block' },
      }}
      setInputValue={(value) => setInputValue(value)}
      paperComponent={
        options.length === 0 && (
          <Typography variant={'body3'}>
            {dictionary.advancedSearch.noResult}
          </Typography>
        )
      }
      paperSx={{
        mt: '1px',
        ml: '10px',
        width: isDesktop ? '425px' : '93%',
      }}
      style={{
        borderRadius: '34px',
        border: '1px solid',
        borderColor: 'primary.shade_200',

        '&&& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '34px',
          bgcolor: '#fff',
          zIndex: -1,
          borderColor: 'primary.shade_200',
          borderWidth: '1px',
        },
        '&&& .MuiInputBase-root input[dropdown="open"] ~ fieldset': {
          borderColor: 'transparent',
        },
      }}
      disableClearIcon
      endAdornment={
        <Stack
          direction={'row'}
          marginRight={'5px'}
          width={'auto'}
          gap={'15px'}
        >
          {inputValue.length > 0 && (
            <Button
              onClick={() => {
                setInputValue('');
                setTextSearchValue('');
              }}
            >
              <Avatar
                alt={dictionary.clearSearchFilter}
                src={PurpleX}
                sx={{ width: '20px', height: '20px' }}
              />
            </Button>
          )}
          <Avatar
            alt=''
            src={PurpleSearchIcon}
            sx={{
              width: isDesktop ? '37px' : '31px',
              height: isDesktop ? '37px' : '31px',
            }}
            onClick={() => submitSearch(inputValue)}
          />
        </Stack>
      }
    />
  );
};
